import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Holds a page with unsaved edits in place: closing or reloading the tab gets
 * the browser's own "leave site?" prompt, and a click on a link to another page
 * of the app is held until the person confirms.
 *
 * Links rather than the router: the app runs on `BrowserRouter`, which has no
 * navigation blocker, and every way off a dashboard page that loses work is a
 * link — the sidebar, the header. Held links are clicked again on confirmation,
 * so whatever else they do (the mobile menu closing itself) still happens.
 *
 * Returns `pending` (true while a link is waiting) with `proceed` and `stay`,
 * for the caller to render its confirmation however it likes.
 */
export const useUnsavedChangesGuard = (dirty) => {
  const [pending, setPending] = useState(false);
  const heldLink = useRef(null);
  const letThrough = useRef(false);

  useEffect(() => {
    if (!dirty) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Still required by some browsers to show the prompt at all.
      event.returnValue = "";
    };

    const onClick = (event) => {
      if (letThrough.current) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!link || link.hasAttribute("download")) return;
      if (link.target && link.target !== "_self") return;

      const url = new URL(link.href, window.location.href);
      // Another site: the browser's own beforeunload prompt covers it.
      if (url.origin !== window.location.origin) return;
      // Same page, another anchor: nothing is lost.
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      event.preventDefault();
      event.stopPropagation();
      heldLink.current = link;
      setPending(true);
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    // Capture, on document: before React's own handler on the root sees it.
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);

  const proceed = useCallback(() => {
    const link = heldLink.current;
    heldLink.current = null;
    setPending(false);
    if (!link?.isConnected) return;
    letThrough.current = true;
    try {
      link.click();
    } finally {
      letThrough.current = false;
    }
  }, []);

  const stay = useCallback(() => {
    heldLink.current = null;
    setPending(false);
  }, []);

  return { pending, proceed, stay };
};
